import type { ReactNode } from 'react';
import type { DevicePreview } from '../state/builderStore';

/**
 * Device-preview frame (build-plan §4.4, §4.1 D16) — CSS width-framing, NO iframe.
 *
 * The same React tree stays mounted; we only constrain the canvas wrapper's `max-width` so the real
 * components reflow responsively (desktop = full/820, tablet = 768, mobile = 390). The width change is
 * smoothly transitioned, and the transition is disabled under `prefers-reduced-motion` (handled in CSS:
 * `.bld-deviceframe` carries the transition, `@media (prefers-reduced-motion: reduce)` removes it).
 *
 * `data-device` is set on the frame so a section component can read it for responsive behaviour without
 * a viewport resize, and tablet/mobile get a subtle device chrome (shadow + rounded corners).
 */
const DEVICE_MAX_WIDTH: Record<DevicePreview, number | null> = {
  desktop: null, // full canvas column (capped by .bld-canvas max-width)
  tablet: 768,
  mobile: 390,
};

export function DeviceFrame({ device, children }: { device: DevicePreview; children: ReactNode }) {
  const maxWidth = DEVICE_MAX_WIDTH[device];
  return (
    <div
      className={`bld-deviceframe bld-deviceframe--${device}`}
      data-device={device}
      style={maxWidth != null ? { maxWidth: `${maxWidth}px` } : undefined}
    >
      {children}
    </div>
  );
}
